/**
 * MentorRequiredModal.tsx
 * Phase 3, Item 7 — Skill-gate mentor picker modal.
 *
 * Opened by AssignEmployeesDialog when the backend returns HTTP 409 skill_gate.
 *
 * Shows:
 *   - Which user(s) are blocked and their skill level.
 *   - An inline Autocomplete listing eligible L3+ mentors for the task type.
 *
 * On confirm: calls onAddMentor(mentor) so the parent dialog can append the
 * mentor to its selected list. The user then clicks "Assign" again, which
 * re-sends with the mentor included → backend gate passes.
 *
 * Copy: "This person's skill is L{n}. Add a mentor (L3+) to proceed."
 *
 * W2-8 (P-15, warn-only): when the backend gate ran per staffed week, each
 * blocked user carries `uncoveredWeeks` and the row names them ("Weeks of
 * Jul 13, Jul 20 have no mentor (L3+)."). The gate is advisory — when the
 * caller supplies `onAssignAnyway`, an "Assign anyway" button lets the planner
 * proceed without a mentor (re-sends with confirmSkillGate: true), mirroring
 * the overbook dialog's "Save anyway".
 *
 * Wave-3 R3: `onAddMentor` is optional. The drag-to-extend path (which has no
 * add-a-mentor mechanism) omits it — the mentor picker + "Add Mentor" button are
 * hidden, leaving the blocked-user explanation + "Assign anyway" / "Cancel".
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { School, Warning } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  fetchEligibleMentors,
  type SkillGateBlockedUser,
  type EligibleMentor,
} from '../../services/scheduleAssignments';
import { fmtShortDate } from '../../utils/weekUtils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  open: boolean;
  /** The task type key, used to fetch eligible mentors. */
  taskTypeKey: string | null;
  /** Users blocked by the skill gate. */
  blockedUsers: SkillGateBlockedUser[];
  /**
   * Called when the user selects a mentor and clicks "Add Mentor". OPTIONAL:
   * omit on flows with no add-a-mentor mechanism (drag-to-extend, Wave-3 R3) —
   * the picker + "Add Mentor" button are hidden, leaving Cancel / Assign anyway.
   */
  onAddMentor?: (mentor: EligibleMentor) => void;
  /** Called when the user cancels without selecting. */
  onClose: () => void;
  /**
   * W2-8 (P-15, warn-only): proceed WITHOUT a mentor. When provided, an
   * "Assign anyway" button appears — the caller re-sends the assignment with
   * confirmSkillGate: true. Omit to keep the old add-a-mentor-or-cancel flow.
   */
  onAssignAnyway?: () => void;
}

/** Per-user message; names the uncovered weeks when the gate ran per-week. */
function blockedUserMessage(u: SkillGateBlockedUser): string {
  const weeks = u.uncoveredWeeks ?? [];
  if (weeks.length > 0) {
    const labels = weeks.map(fmtShortDate).join(', ');
    const plural = weeks.length > 1;
    return `'s skill is L${u.level ?? '?'}. ${plural ? 'Weeks' : 'Week'} of ${labels} ${plural ? 'have' : 'has'} no mentor (L3+).`;
  }
  return `'s skill is L${u.level ?? '?'}. Add a mentor (L3+) to proceed.`;
}

// ---------------------------------------------------------------------------
// Level chip
// ---------------------------------------------------------------------------

function LevelChip({ level }: { level: number | null }) {
  const theme = useTheme();
  const label = level !== null ? `L${level}` : 'L?';

  const colorMap: Record<number, string> = {
    1: theme.schedule.skillLevel1,
    2: theme.schedule.skillLevel2,
    3: theme.schedule.skillLevel3,
    4: theme.schedule.skillLevel4,
  };
  const color = level !== null ? (colorMap[level] ?? theme.schedule.noActivity) : theme.schedule.noActivity;

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontSize: '0.68rem',
        height: 20,
        fontWeight: 700,
        backgroundColor: alpha(color, 0.15),
        color: color,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MentorRequiredModal: React.FC<Props> = ({
  open,
  taskTypeKey,
  blockedUsers,
  onAddMentor,
  onClose,
  onAssignAnyway,
}) => {
  const theme = useTheme();

  const [mentors, setMentors] = useState<EligibleMentor[]>([]);
  const [loadingMentors, setLoadingMentors] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedMentor, setSelectedMentor] = useState<EligibleMentor | null>(null);

  // Only the add-a-mentor flow needs the picker; the drag-extend flow omits it.
  const canAddMentor = Boolean(onAddMentor);

  // Load eligible mentors when the modal opens (add-a-mentor flow only)
  useEffect(() => {
    if (!open || !taskTypeKey || !canAddMentor) return;
    let cancelled = false;
    setLoadingMentors(true);
    setFetchError(null);
    setSelectedMentor(null);

    fetchEligibleMentors(taskTypeKey, 3)
      .then((data) => { if (!cancelled) setMentors(data); })
      .catch((err) => {
        if (!cancelled) {
          setFetchError(err instanceof Error ? err.message : 'Failed to load mentors.');
        }
      })
      .finally(() => { if (!cancelled) setLoadingMentors(false); });

    return () => { cancelled = true; };
  }, [open, taskTypeKey, canAddMentor]);

  const handleConfirm = () => {
    if (!selectedMentor || !onAddMentor) return;
    onAddMentor(selectedMentor);
  };

  // Build the per-user message — one line per blocked user
  const firstBlocked = blockedUsers[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${theme.border.radius}px`,
          border: `1px solid ${theme.schedule.conflictHard}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1,
          borderBottom: `1px solid ${theme.border.color}`,
        }}
      >
        <Warning sx={{ color: theme.schedule.conflictHard, fontSize: 22 }} />
        <Typography variant="subtitle1" fontWeight={700}>
          Mentor Required
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {/* Per-blocked-user messages */}
        {blockedUsers.map((u) => (
          <Box
            key={u.userKey}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
              p: 1.25,
              borderRadius: `${theme.border.radius}px`,
              backgroundColor: alpha(theme.schedule.conflictHard, 0.06),
              border: `1px solid ${alpha(theme.schedule.conflictHard, 0.2)}`,
            }}
          >
            <LevelChip level={u.level} />
            <Typography variant="body2" sx={{ fontSize: '0.83rem' }}>
              <strong>{u.displayName}</strong>
              {blockedUserMessage(u)}
            </Typography>
          </Box>
        ))}

        {canAddMentor && (
          <>
            {fetchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {fetchError}
              </Alert>
            )}

            {/* Mentor picker */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.75, mt: 1 }}
            >
              Select an L3+ mentor to add to the assignment:
            </Typography>
            <Autocomplete
              loading={loadingMentors}
              options={mentors}
              value={selectedMentor}
              getOptionLabel={(m) => `${m.DisplayName} (L${m.Level})`}
              isOptionEqualToValue={(a, b) => a.AppUserKey === b.AppUserKey}
              onChange={(_e, val) => setSelectedMentor(val)}
              noOptionsText={
                taskTypeKey
                  ? 'No L3+ users found for this task type.'
                  : 'No task type resolved.'
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select mentor"
                  placeholder="Search by name…"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingMentors && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1, fontSize: '0.83rem' }}>
                    {option.DisplayName}
                  </Typography>
                  <LevelChip level={option.Level} />
                </Box>
              )}
            />
          </>
        )}

        {firstBlocked && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1.25, lineHeight: 1.5 }}
          >
            {canAddMentor ? (
              <>
                The mentor will be added to the assignment list. Click
                {' '}<strong>Assign</strong> again to proceed.
                {onAssignAnyway && (
                  <> Or <strong>Assign anyway</strong> to proceed without a mentor.</>
                )}
              </>
            ) : (
              <>Add a mentor to these weeks, or <strong>Assign anyway</strong> to proceed without one.</>
            )}
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${theme.border.color}`,
          gap: 1,
        }}
      >
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        {/* W2-8 (warn-only): the mentor gate is advisory — proceed without one. */}
        {onAssignAnyway && (
          <Button onClick={onAssignAnyway} size="small" color="warning" variant="outlined">
            Assign anyway
          </Button>
        )}
        {canAddMentor && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!selectedMentor}
            size="small"
            startIcon={<School />}
            sx={{
              backgroundColor: theme.schedule.employeeAlloc,
              '&:hover': { backgroundColor: alpha(theme.schedule.employeeAlloc, 0.85) },
            }}
          >
            Add Mentor
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
